import { useEffect, useState } from "react";
import {
    Mail,
    RefreshCw,
    Trash2,
    Send,
    Loader2,
    Inbox,
    Megaphone,
    AlertTriangle,
    Users,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import PublicLayout from "@/components/layout/PublicLayout";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ status }) {
    const map = {
        pending: "bg-amber-50 text-amber-800 border-amber-200",
        sent: "bg-emerald-50 text-emerald-800 border-emerald-200",
        dismissed: "bg-bl-line text-bl-muted",
        failed: "bg-rose-50 text-rose-800 border-rose-200",
    };
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 text-[10px] rounded-full border uppercase tracking-wide ${
                map[status] || "bg-bl-line text-bl-muted"
            }`}
        >
            {status}
        </span>
    );
}

export default function AdminNewsletterBacklog() {
    const [rows, setRows] = useState([]);
    const [pending, setPending] = useState(0);
    const [sent, setSent] = useState(0);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [broadcastOpen, setBroadcastOpen] = useState(false);
    const [broadcastPreview, setBroadcastPreview] = useState(null);
    const [broadcastLoading, setBroadcastLoading] = useState(false);
    const [broadcastAudience, setBroadcastAudience] = useState("engaged");
    const [broadcastFiring, setBroadcastFiring] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/admin/newsletter/backlog`, {
                withCredentials: true,
            });
            setRows(r.data.entries || []);
            setPending(r.data.pending_count || 0);
            setSent(r.data.sent_count || 0);
        } catch {
            toast.error("Failed to load backlog.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const dismiss = async (id) => {
        if (!window.confirm("Dismiss this newsletter entry? It won't be sent.")) return;
        setBusyId(id);
        try {
            await axios.post(
                `${API}/admin/newsletter/backlog/${id}/dismiss`,
                {},
                { withCredentials: true },
            );
            toast.success("Dismissed.");
            await refresh();
        } catch {
            toast.error("Dismiss failed.");
        } finally {
            setBusyId(null);
        }
    };

    const sendNow = async (id) => {
        if (
            !window.confirm(
                "Send this newsletter to the engaged segment NOW? (Skips Tuesday gate.)",
            )
        )
            return;
        setBusyId(id);
        try {
            const r = await axios.post(
                `${API}/admin/newsletter/backlog/${id}/send-now`,
                { audience: "engaged" },
                { withCredentials: true },
            );
            toast.success(
                `Sent to engaged segment · ${r.data.sent} delivered · ${
                    r.data.suppressed_save_resume +
                    r.data.suppressed_segment
                } suppressed.`,
            );
            await refresh();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Send-now failed.",
            );
        } finally {
            setBusyId(null);
        }
    };

    const openBroadcast = async () => {
        setBroadcastOpen(true);
        setBroadcastPreview(null);
        setBroadcastLoading(true);
        try {
            const r = await axios.get(
                `${API}/admin/newsletter/broadcast-preview`,
                { withCredentials: true },
            );
            setBroadcastPreview(r.data);
        } catch {
            toast.error("Failed to load broadcast preview.");
            setBroadcastOpen(false);
        } finally {
            setBroadcastLoading(false);
        }
    };

    const fireBroadcast = async () => {
        setBroadcastFiring(true);
        try {
            const r = await axios.post(
                `${API}/admin/newsletter/run-picker`,
                { force: true, audience: broadcastAudience },
                { withCredentials: true },
            );
            if (r.data.skipped) {
                toast.info(
                    `Picker skipped: ${r.data.reason || "no_pending_backlog"}`,
                );
            } else {
                const phase = r.data.engaged_phase || {};
                toast.success(
                    `Broadcast fired · ${r.data.article_title || "Untitled"} · ${
                        phase.sent || 0
                    } delivered to ${broadcastAudience} (${
                        phase.subscribers_checked || 0
                    } checked).`,
                );
            }
            setBroadcastOpen(false);
            await refresh();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Broadcast failed.",
            );
        } finally {
            setBroadcastFiring(false);
        }
    };

    return (
        <PublicLayout>
            <div className="bl-section py-10">
                <div className="bl-container max-w-5xl space-y-5" data-testid="admin-newsletter-backlog">
                <div className="bl-card p-5 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-bl-muted">
                            Customer email
                        </div>
                        <h1 className="font-display font-semibold text-2xl text-bl tracking-tight">
                            Newsletter backlog
                        </h1>
                        <p className="text-xs text-bl-muted mt-1 max-w-2xl leading-relaxed">
                            Every published content package adds an email entry here. Each
                            Tuesday at 10:00 UTC, one random pending entry is picked and
                            sent — engaged subscribers first, then the rest if open rates
                            clear 25%.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={refresh}
                            className="bl-btn-ghost text-xs gap-1"
                            disabled={loading}
                            data-testid="newsletter-refresh"
                        >
                            <RefreshCw
                                className={`h-3 w-3 ${
                                    loading ? "animate-spin" : ""
                                }`}
                            />
                            Refresh
                        </button>
                        <button
                            onClick={openBroadcast}
                            className="bl-btn-primary text-xs gap-1"
                            data-testid="newsletter-broadcast-now"
                        >
                            <Megaphone className="h-3 w-3" />
                            Send broadcast now
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bl-card p-4">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-bl-muted">
                            Pending
                        </div>
                        <div
                            className="font-display font-semibold text-3xl text-bl mt-1"
                            data-testid="newsletter-pending-count"
                        >
                            {pending}
                        </div>
                        <div className="text-xs text-bl-muted mt-1">
                            Ready to be picked next Tuesday
                        </div>
                    </div>
                    <div className="bl-card p-4">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-bl-muted">
                            Sent (history)
                        </div>
                        <div
                            className="font-display font-semibold text-3xl text-bl mt-1"
                            data-testid="newsletter-sent-count"
                        >
                            {sent}
                        </div>
                    </div>
                    <div className="bl-card p-4">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-bl-muted">
                            Cadence
                        </div>
                        <div className="font-display font-semibold text-3xl text-bl mt-1">
                            1×/wk
                        </div>
                        <div className="text-xs text-bl-muted mt-1">
                            Tuesday 10:00 UTC · 24h marketing cap
                        </div>
                    </div>
                </div>

                <div className="bl-card overflow-hidden">
                    <div className="px-5 py-4 border-b border-bl-line flex items-center gap-2">
                        <Inbox className="h-4 w-4 text-bl-muted" />
                        <h2 className="font-display font-semibold text-base text-bl tracking-tight">
                            Queue
                        </h2>
                    </div>
                    {loading ? (
                        <div className="p-8 text-center text-sm text-bl-muted">
                            Loading…
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="p-10 text-center text-sm text-bl-muted">
                            <Mail className="h-8 w-8 mx-auto text-bl-muted/40 mb-3" />
                            No backlog entries yet. Run auto-publish on a content
                            package to enqueue one.
                        </div>
                    ) : (
                        <table className="w-full text-sm" data-testid="newsletter-table">
                            <thead className="bg-bl-bg/40">
                                <tr className="text-left text-[10px] uppercase tracking-wide text-bl-muted">
                                    <th className="px-5 py-3">Article</th>
                                    <th className="px-3 py-3">Subject</th>
                                    <th className="px-3 py-3">Status</th>
                                    <th className="px-3 py-3">Created</th>
                                    <th className="px-3 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr
                                        key={row.backlog_id}
                                        className="border-t border-bl-line hover:bg-bl-bg/20"
                                        data-testid={`newsletter-row-${row.backlog_id}`}
                                    >
                                        <td className="px-5 py-3">
                                            <div className="text-bl font-medium">
                                                {row.article_title}
                                            </div>
                                            <a
                                                href={`/learn/${row.article_slug}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-[11px] text-bl-muted underline"
                                            >
                                                /learn/{row.article_slug}
                                            </a>
                                        </td>
                                        <td className="px-3 py-3 text-bl text-xs max-w-xs truncate">
                                            {row.subject}
                                        </td>
                                        <td className="px-3 py-3">
                                            <StatusBadge status={row.status} />
                                        </td>
                                        <td className="px-3 py-3 text-bl-muted text-xs">
                                            {row.created_at
                                                ? new Date(row.created_at).toLocaleString()
                                                : "—"}
                                        </td>
                                        <td className="px-3 py-3 text-right">
                                            {row.status === "pending" && (
                                                <div className="flex items-center gap-1 justify-end">
                                                    <button
                                                        onClick={() =>
                                                            sendNow(row.backlog_id)
                                                        }
                                                        disabled={
                                                            busyId === row.backlog_id
                                                        }
                                                        className="bl-btn-ghost text-[11px] gap-1"
                                                        data-testid={`newsletter-send-now-${row.backlog_id}`}
                                                    >
                                                        <Send className="h-3 w-3" />
                                                        Send now
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            dismiss(row.backlog_id)
                                                        }
                                                        disabled={
                                                            busyId === row.backlog_id
                                                        }
                                                        className="bl-btn-ghost text-[11px] gap-1 text-rose-700"
                                                        data-testid={`newsletter-dismiss-${row.backlog_id}`}
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                        Dismiss
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                </div>
            </div>

            <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
                <DialogContent className="max-w-lg" data-testid="broadcast-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 font-display tracking-tight text-bl">
                            <Megaphone className="h-4 w-4 text-bl-accent" />
                            Send weekly broadcast now
                        </DialogTitle>
                        <DialogDescription className="text-xs text-bl-muted leading-relaxed">
                            Picks one random <span className="font-medium">pending</span> backlog
                            entry and broadcasts it immediately. This bypasses the
                            Tuesday 10:00 UTC gate.
                        </DialogDescription>
                    </DialogHeader>

                    {broadcastLoading ? (
                        <div className="py-8 text-center text-sm text-bl-muted">
                            <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" />
                            Loading preview…
                        </div>
                    ) : broadcastPreview ? (
                        <div className="space-y-4 text-sm">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bl-card p-3">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-bl-muted">
                                        Pending
                                    </div>
                                    <div className="font-display font-semibold text-2xl text-bl mt-1" data-testid="broadcast-preview-pending">
                                        {broadcastPreview.pending_count}
                                    </div>
                                    <div className="text-[11px] text-bl-muted">
                                        ready to broadcast
                                    </div>
                                </div>
                                <div className="bl-card p-3">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-bl-muted">
                                        Subscribers
                                    </div>
                                    <div className="font-display font-semibold text-2xl text-bl mt-1" data-testid="broadcast-preview-subscribers">
                                        {broadcastPreview.audience?.total_subscribers ?? 0}
                                    </div>
                                    <div className="text-[11px] text-bl-muted">
                                        engaged {broadcastPreview.audience?.engaged_subscribers ?? 0}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-bl-muted mb-2">
                                    Audience
                                </div>
                                <div className="space-y-1.5">
                                    <label className="flex items-start gap-2 text-xs cursor-pointer p-2 rounded border border-bl-line hover:bg-bl-bg/30">
                                        <input
                                            type="radio"
                                            name="bcast-audience"
                                            value="engaged"
                                            checked={broadcastAudience === "engaged"}
                                            onChange={() => setBroadcastAudience("engaged")}
                                            data-testid="broadcast-audience-engaged"
                                            className="mt-0.5"
                                        />
                                        <span>
                                            <span className="font-medium text-bl">
                                                Engaged only ({broadcastPreview.audience?.engaged_subscribers ?? 0})
                                            </span>
                                            <span className="block text-bl-muted">
                                                Opened any email in the last 30 days + new signups. Safer default.
                                            </span>
                                        </span>
                                    </label>
                                    <label className="flex items-start gap-2 text-xs cursor-pointer p-2 rounded border border-bl-line hover:bg-bl-bg/30">
                                        <input
                                            type="radio"
                                            name="bcast-audience"
                                            value="all"
                                            checked={broadcastAudience === "all"}
                                            onChange={() => setBroadcastAudience("all")}
                                            data-testid="broadcast-audience-all"
                                            className="mt-0.5"
                                        />
                                        <span>
                                            <span className="font-medium text-bl">
                                                All subscribers ({broadcastPreview.audience?.total_subscribers ?? 0})
                                            </span>
                                            <span className="block text-bl-muted">
                                                Includes monthly-only segment. Higher reach, slightly higher unsubscribe risk.
                                            </span>
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {broadcastPreview.pending_count > 0 ? (
                                <div className="bl-card p-3 bg-bl-bg/20">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-bl-muted mb-2 flex items-center gap-1">
                                        <Inbox className="h-3 w-3" />
                                        Pending queue (random pick from these)
                                    </div>
                                    <ul className="space-y-1" data-testid="broadcast-preview-list">
                                        {(broadcastPreview.pending_preview || []).map((p) => (
                                            <li key={p.backlog_id} className="text-xs text-bl truncate">
                                                · {p.article_title}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <div className="bl-card p-3 bg-amber-50 border-amber-200 flex items-start gap-2">
                                    <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5" />
                                    <div className="text-xs text-amber-900">
                                        No pending backlog entries. Auto-publish a content
                                        package first to enqueue one.
                                    </div>
                                </div>
                            )}

                            <div className="text-[11px] text-bl-muted leading-relaxed flex items-start gap-2">
                                <Users className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                <span>{broadcastPreview.cadence_note}</span>
                            </div>
                        </div>
                    ) : null}

                    <DialogFooter className="gap-2">
                        <button
                            onClick={() => setBroadcastOpen(false)}
                            className="bl-btn-ghost text-xs"
                            disabled={broadcastFiring}
                            data-testid="broadcast-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={fireBroadcast}
                            className="bl-btn-primary text-xs gap-1"
                            disabled={
                                broadcastFiring ||
                                broadcastLoading ||
                                !broadcastPreview ||
                                broadcastPreview.pending_count === 0
                            }
                            data-testid="broadcast-confirm"
                        >
                            {broadcastFiring ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <Megaphone className="h-3 w-3" />
                            )}
                            Confirm and send to {broadcastAudience}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PublicLayout>
    );
}
